import type { ReactElement } from 'react';
import { styled } from '@mui/material/styles';
import { Tab } from '@mui/material';
import dayjs from 'dayjs';
import { UseFormReturn } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import { FileWrapper } from 'helpers/classes';
import { handleImageQualityThresholds } from 'helpers/image-quality-thresholds';
import {
  ITIL_CLOSE_MARKER,
  splitCommentWithItilCloseBlock
} from 'features/itilGenius';

export type TTabs = 'detail' | 'close' | 'movements' | 'documentation';
export type TChangeTabs =
  | 'nameDetail'
  | 'homeWork'
  | 'approvals'
  | 'approvalsVote'
  | 'lavelRisk';

type Fields = {
  description: string;
  fileName: string;
  fileBase64: string;
  size: number;
};

interface ITabs {
  label: string;
  icon: ReactElement;
  component: ReactElement;
  type?: string;
  disabled?: boolean;
}

export const formatImagesAllow: string[] = ['.jpg', '.jpeg', '.png'];
export const formatNotImagesAllow: string[] = [
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.pdf'
];

/**
 * Carga una imagen por URL y la re-exporta como JPEG dataURL vía canvas.
 * pdfMake valida PNG de forma estricta; archivos truncados en blob (p. ej. 64 KB
 * sin IEND) fallan en export masivo si se pasa la URL cruda. El canvas del
 * navegador tolera mejor PNGs incompletos y unifica el formato a JPEG.
 * SAIMONOPE-752
 */
export function toJpgBase64(url: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas 2d context unavailable'));
          return;
        }
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/jpeg', 0.92));
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = (err) => {
      reject(err);
    };
    img.src = url;
  });
}

/** Convierte URLs de evidencia a JPEG dataURL; omite las que fallen al cargar. */
export async function toJpgBase64List(urls: string[]): Promise<string[]> {
  const results = await Promise.all(
    urls.map(async (url) => {
      try {
        return await toJpgBase64(url);
      } catch (err) {
        console.warn('SAIMONOPE-752: imagen omitida en PDF (corrupta o inaccesible)', url, err);
        return null;
      }
    })
  );
  return results.filter((item): item is string => !!item);
}

/**
 * Formatea una fecha en formato local con zona horaria del usuario.
 *
 * Convierte una fecha UTC a formato local legible (DD/MM/YYYY HH:mm:ss).
 *
 * @param {string | Date} date - Fecha a formatear.
 * @returns {string} Fecha formateada o '-----' si no hay fecha.
 *
 * @example
 * formatDate('2023-12-01T10:30:00Z') // '01/12/2023 10:30:00'
 */
export const formatDate = (date: string | Date) => {
  // Obtener la zona horaria del usuario solo una vez
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return date
    ? dayjs.utc(date).local().tz(userTimeZone).format('DD/MM/YYYY HH:mm:ss')
    : '-----';
};

export const DetailTicketTab = styled(Tab)(() => ({
  '&.MuiTab-root': {
    height: '43px',
    minHeight: '43px',
    minWidth: '210px',
    padding: '9px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#9C9C9C',
    borderRadius: '5px',
    margin: '0px 5px 5px 5px',
    justifyContent: 'start',
    '@media (max-width: 450px)': {
      height: '33px',
      minHeight: '33px',
      minWidth: '180px',
      paddingX: '5px',
      width: '100%'
    }
  },
  '&.Mui-selected': {
    borderColor: '#1976d2'
  }
}));

const rotateImage = (
  rawBase64: string,
  file: FileWrapper,
  size: number,
  form: UseFormReturn<Fields>
) => {
  const image = new Image();
  image.src = rawBase64;
  image.onload = () => {
    if (image.height > image.width) {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.width = image.height;
      canvas.height = image.width;

      // Rotate in anticlockwise direction
      context.rotate((-90 * Math.PI) / 180);
      context.translate(-canvas.height, 0);

      context.drawImage(image, 0, 0);

      const newRawBase64 = canvas.toDataURL('image/jpeg', 0.95);

      // eslint-disable-next-line no-param-reassign
      rawBase64 = newRawBase64;
    }
    const commaIndex = rawBase64.indexOf(',');
    const base64Data = rawBase64.slice(commaIndex + 1);
    form.setValue('fileBase64', base64Data);
    form.setValue('fileName', file.file.name);
    form.setValue('size', size);
  };
};

/**
 * Configura y valida archivos para ser subidos como adjuntos.
 *
 * Valida el tipo de archivo, tamaño, calidad de imagen y convierte a base64.
 * Maneja rotación automática de imágenes verticales.
 *
 * @param {UseFormReturn<Fields>} form - Instancia del formulario donde se guardarán los datos del archivo.
 * @param {function} callbackError - Función callback para manejar errores de validación.
 * @returns {object} Objeto con función setFile para procesar archivos.
 *
 * @example
 * const { setFile } = settingFiles(form, (error) => showError(error));
 * setFile(files);
 */
export function settingFiles(
  form: UseFormReturn<Fields>,
  callbackError: (key: string, time?: number) => void
) {
  const setFile = async (files: FileWrapper[]) => {
    if (files.length > 0) {
      const file = files[0];
      const base64 = await file.getBase64();
      const size = file.getMbSize();
      const fileType = file?.file?.type.split('/')[0];
      const fileExtension = file?.file?.name.split('.').pop()?.toLowerCase();
      const validateIdImageAccepted = formatImagesAllow.includes(
        `.${fileExtension?.toLowerCase()}` || ''
      );
      const validateIdNotImageAccepted = formatNotImagesAllow.includes(
        `.${fileExtension?.toLowerCase()}` || ''
      );

      // Validar que la extensión esté en alguna de las listas permitidas
      const isValidExtension = validateIdImageAccepted || validateIdNotImageAccepted;

      if (!isValidExtension) {
        // Extension not allowed
        callbackError('ticket.ticketDetail.invalidateFormat');
      } else if (fileType === 'image' && !validateIdImageAccepted) {
        // Image validation failed
        callbackError('ticket.ticketDetail.invalidateFormat');
      } else if (fileType === 'application' && !validateIdNotImageAccepted) {
        // NOT Image validation failed
        callbackError('errorMessages.invalidateFormatNotImage');
      } else if (size > 10) {
        // Size validation failed
        callbackError('ticket.ticketDetail.sizeExceeded10mb');
      } else {
        // All validations passed
        if (fileType === 'image') {
          const base64string = base64.toString();

          const handleImageAccepted = (isAccepted: boolean) => {
            if (isAccepted) rotateImage(base64string, file, size, form);
            else {
              callbackError('ticket.ticketDetail.invalidateImage', 2500);
              form.setValue('fileBase64', '');
              form.setValue('fileName', '');
              form.setValue('size', null);
            }
          };
          handleImageQualityThresholds(base64string, handleImageAccepted);
          return;
        }
        const newBase64 = base64
          .toString()
          .replace(`data:${file.file.type};base64,`, '');
        form.setValue('fileBase64', newBase64);
        form.setValue('fileName', file.file.name);
        form.setValue('size', size);
      }
    } else {
      // No file selected
      callbackError('ticket.ticketDetail.noFileSelected');
    }
  };
  return { setFile };
}

export const StyledPre = styled('pre')(() => ({
  backgroundColor: '#fff',
  border: '1px solid rgba(118, 118, 118, 0.3)',
  borderRadius: '4px',
  padding: '0 1rem',
  whiteSpace: ' pre-wrap',
  overflow: 'auto',
  fontFamily: 'Roboto',
  fontSize: '14px'
}));

const Campo = styled('span')(() => ({
  fontWeight: '500',
  color: '#0E335C'
}));

/**
 * Renderiza texto formateado con campos destacados en negrita.
 *
 * Divide el texto por líneas y resalta los campos que están antes del caracter ':'.
 *
 * @param {string} text - Texto a formatear.
 * @returns {JSX.Element[]} Array de elementos JSX con texto formateado.
 *
 * @example
 * renderFormattedText('Campo: Valor\nOtro: Otro valor')
 * // Renderiza <p><span>Campo:</span> Valor</p><p><span>Otro:</span> Otro valor</p>
 */
export const renderFormattedText = (text: string) =>
  text?.split('\n').map((line) => {
    const parts = line.split(/:(.+)/);
    if (parts?.length > 1) {
      return (
        <p key={parts[0]}>
          <Campo>{parts[0]}:&nbsp;</Campo>
          {parts[1].trim()}
        </p>
      );
    }
    return <p key={parts[0]}>{line}</p>;
  });

/**
 * Obtiene el índice de una pestaña basado en su tipo o número.
 *
 * Busca el índice de una pestaña en el array de pestañas disponibles.
 *
 * @param {TTabs | number} tab - Tipo de pestaña o índice numérico.
 * @param {ITabs[]} tabs - Array de pestañas disponibles.
 * @returns {number} Índice de la pestaña encontrada o 0 por defecto.
 *
 * @example
 * getTab('detail', tabs) // Retorna el índice de la pestaña 'detail'
 */
export const getTab = (tab: TTabs | number, tabs: ITabs[]) => {
  const index = tabs.findIndex((item) => item?.type === tab);
  if (typeof tab === 'number') return tab;
  if (index && index !== -1) return index;
  return 0;
};

export const getTabChange = (tab: TChangeTabs | number, tabs: ITabs[]) => {
  const index = tabs.findIndex((item) => item?.type === tab);
  if (typeof tab === 'number') return tab;
  if (index && index !== -1) return index;
  return 0;
};

const preStyle = {
  whiteSpace: 'pre-wrap' as const,
  wordWrap: 'break-word' as const,
  overflowWrap: 'break-word' as const
};

const renderClosureFieldLines = (text: string) => {
  const lines = text.split('\n').filter((line) => line.trim() !== '');

  return lines.map((line) => {
    const match = line.match(/^([^:]+):\s(.+)$/);
    if (match) {
      const key = match[1].trim();
      const value = match[2].trim();
      return (
        <pre key={uuidv4()} style={preStyle}>
          <strong>{key}: </strong>
          {value}
        </pre>
      );
    }
    return (
      <pre key={uuidv4()} style={preStyle}>
        {line}
      </pre>
    );
  });
};

export const formatClosureComment = (message: string) => {
  const { original, itilText } = splitCommentWithItilCloseBlock(message);
  const fieldLines = original ? renderClosureFieldLines(original) : [];

  if (!itilText) {
    return fieldLines;
  }

  return [
    ...fieldLines,
    <div
      key="itil-close-block"
      style={{
        marginTop: '12px',
        padding: '12px',
        borderRadius: '4px',
        border: '1px solid #e0e0e0',
        backgroundColor: '#f5f5f5'
      }}
    >
      <pre style={{ ...preStyle, margin: '0 0 8px', fontWeight: 600 }}>
        {ITIL_CLOSE_MARKER.replace(':', '')}
      </pre>
      <pre style={{ ...preStyle, margin: 0 }}>{itilText}</pre>
    </div>
  ];
};
